import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { CheckCircle, ArrowRight, Sparkles, RefreshCw } from "lucide-react";
import { OAuthPlatformCards } from "@/components/OAuthPlatformCards";
import { PlatformSelectionModal } from "@/components/PlatformSelectionModal";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { CheckinReturnBar } from "@/components/CheckinReturnBar";
import { useIsHotelEdition } from "@/lib/hotelEdition";

export default function PlatformConnections() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const isHotel = useIsHotelEdition();
  const [connectionsProgress, setConnectionsProgress] = useState(0);
  const [selectionPlatform, setSelectionPlatform] = useState<string | null>(null);
  const [selectionModalOpen, setSelectionModalOpen] = useState(false);

  // Query keys here must match what server/routes/late.ts actually redirects with
  // (?pending=<platform> | ?connected=<platform> | ?error=<code>) — the previous
  // oauth_select/oauth_success/oauth_error/platform keys were never sent by that route.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pending = urlParams.get('pending');
    const connected = urlParams.get('connected');
    const error = urlParams.get('error');

    if (pending) {
      setSelectionPlatform(pending);
      setSelectionModalOpen(true);
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (connected) {
      const platformName = connected === 'linkedin' ? 'LinkedIn' : connected === 'facebook' ? 'Facebook' : connected === 'instagram' ? 'Instagram' : connected === 'googlebusiness' ? 'Google My Business' : connected;
      toast({
        title: "Verbindung erfolgreich",
        description: `${platformName} wurde erfolgreich verbunden!`,
      });
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (error) {
      toast({
        title: "Verbindung fehlgeschlagen",
        description: decodeURIComponent(error),
        variant: "destructive",
      });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [toast]);

  const { data: connections = [], refetch: refetchConnections } = useQuery({
    queryKey: ['/api/late/accounts']
  });

  const syncMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('POST', '/api/late/sync-accounts');
      return response;
    },
    onSuccess: (data: any) => {
      toast({
        title: "Sync erfolgreich",
        description: `${data.updated} aktualisiert, ${data.created} neu erstellt`,
      });
      queryClient.invalidateQueries({ queryKey: ['/api/late/accounts'] });
      refetchConnections();
    },
    onError: (error: any) => {
      toast({
        title: "Sync fehlgeschlagen",
        description: error.message || "Konnte Verbindungen nicht synchronisieren",
        variant: "destructive",
      });
    }
  });

  useEffect(() => {
    const totalPlatforms = 4;
    const connectionData = Array.isArray(connections) ? connections : [];
    const connectedPlatforms = connectionData.filter((conn: any) => conn.isActive).length;
    const progress = (connectedPlatforms / totalPlatforms) * 100;
    setConnectionsProgress(progress);
  }, [connections]);

  const connectionData = Array.isArray(connections) ? connections : [];
  const connectedCount = connectionData.filter((conn: any) => conn.isActive).length;
  const totalCount = 4;

  const handleContinueToDashboard = () => {
    navigate('/');
  };

  const handleSkipForNow = () => {
    toast({
      title: "Uebersprungen",
      description: "Sie koennen Ihre Plattformen jederzeit im Dashboard verbinden.",
    });
    navigate('/');
  };

  const handleSelectionSuccess = (platform: string) => {
    refetchConnections();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 dark:from-purple-950 dark:via-blue-950 dark:to-indigo-950">
      <div className="container mx-auto px-4 py-8">
        <CheckinReturnBar />

        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="w-8 h-8 text-purple-600" />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              Social Media Plattformen
            </h1>
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Verbinden Sie Ihre Social-Media-Konten, damit Ihr A-Team für Sie veröffentlichen kann.
          </p>
        </div>

        <Card className="max-w-2xl mx-auto mb-8" data-testid="progress-card">
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Verbindungsfortschritt
                </CardTitle>
                <CardDescription>
                  {connectedCount} von {totalCount} Plattformen verbunden
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => syncMutation.mutate()}
                disabled={syncMutation.isPending}
                data-testid="button-sync-connections"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
                {syncMutation.isPending ? 'Synchronisiere...' : 'Aktualisieren'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Progress value={connectionsProgress} className="h-3" />
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  {Math.round(connectionsProgress)}% abgeschlossen
                </span>
                {connectedCount === totalCount && (
                  <Badge variant="default" className="bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Vollstaendig
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="max-w-6xl mx-auto mb-8">
          <OAuthPlatformCards onPlatformConnect={() => refetchConnections()} />
        </div>

        <div className="flex flex-col sm:flex-row justify-center gap-4 max-w-md mx-auto">
          {connectedCount > 0 && (
            <Button
              onClick={handleContinueToDashboard}
              className="flex-1"
              data-testid="button-continue-dashboard"
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              Zum Dashboard
            </Button>
          )}
          
          <Button
            variant="outline"
            onClick={handleSkipForNow}
            className="flex-1"
            data-testid="button-skip-connections"
          >
            Spaeter verbinden
          </Button>
        </div>

        <div className="max-w-2xl mx-auto mt-12 text-center">
          <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-blue-900 dark:text-blue-100">
                  Warum Plattformen verbinden?
                </h3>
              </div>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {isHotel
                  ? "Durch die Verbindung Ihrer Social Media Konten kann Ihr A-Team Ihre Beiträge einplanen, veröffentlichen und die Wirkung auswerten."
                  : <>Durch die Verbindung Ihrer Social Media Konten kann LinkedIn Lina automatisch 
                optimierte Inhalte erstellen, Posts planen und Ihre Marketing-Performance analysieren.</>}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <PlatformSelectionModal
        platform={selectionPlatform}
        open={selectionModalOpen}
        onClose={() => {
          setSelectionModalOpen(false);
          setSelectionPlatform(null);
        }}
        onSuccess={handleSelectionSuccess}
      />
    </div>
  );
}
